import { useEffect } from 'react';
import { toast } from 'sonner';

const DEFAULT_MAX_MB = 10;
const MB = 1024 * 1024;
const isImage = (f) => /^image\/(jpe?g|png|webp|heic|heif)$/i.test(f.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name);

const compress = async (file, maxBytes) => {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  for (const [dim, q] of [[2500, 0.85], [2000, 0.8], [1600, 0.75]]) {
    const scale = Math.min(1, dim / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= maxBytes) {
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
    }
  }
  throw new Error('still too big');
};

const fmt = (b) => `${(b / MB).toFixed(1).replace('.', ',')}MB`;

// Antes do React: fotos acima do limite são comprimidas; outros ficheiros grandes são recusados com aviso.
export const FileSizeGuard = () => {
  useEffect(() => {
    const onChange = async (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement) || el.type !== 'file' || !el.files?.length) return;
      if (el.dataset.fsgPass === '1') { delete el.dataset.fsgPass; return; }
      const maxMb = el.dataset.maxMb !== undefined ? Number(el.dataset.maxMb) : DEFAULT_MAX_MB;
      if (!maxMb) return;
      const maxBytes = maxMb * MB;
      const files = [...el.files];
      if (!files.some((f) => f.size > maxBytes)) return;
      e.stopImmediatePropagation();
      e.stopPropagation();

      const out = [];
      const rejected = [];
      const tid = toast.loading('A otimizar foto…');
      for (const f of files) {
        if (f.size <= maxBytes) { out.push(f); continue; }
        if (!isImage(f)) { rejected.push(f); continue; }
        try {
          const c = await compress(f, maxBytes);
          out.push(c);
          toast.success(`Foto otimizada: ${fmt(f.size)} → ${fmt(c.size)}`, { duration: 5000 });
        } catch {
          rejected.push(f);
        }
      }
      toast.dismiss(tid);
      if (rejected.length) {
        const names = rejected.map((f) => `"${f.name}" (${fmt(f.size)})`).join(', ');
        toast.error(`Ficheiro demasiado grande: ${names}. O tamanho máximo é ${maxMb}MB.`, {
          description: 'Reduza o tamanho (ex.: comprimir o PDF) e tente novamente.',
          duration: 8000,
        });
      }
      if (!out.length) { el.value = ''; return; }
      const dt = new DataTransfer();
      out.forEach((f) => dt.items.add(f));
      el.files = dt.files;
      el.dataset.fsgPass = '1';
      el.dispatchEvent(new Event('change', { bubbles: true }));
    };
    window.addEventListener('change', onChange, true);
    return () => window.removeEventListener('change', onChange, true);
  }, []);
  return null;
};
